import { type BoxDisplayModel } from '@makeswift/controls'
import {
  createBox as createBoxWithoutScroll,
  CreateBoxArgs,
  getBox as getBoxWithoutScroll,
  withScroll,
} from 'css-box-model'

import { type Action, type UnknownAction, isKnownAction } from '../../actions'
import { ReadWriteActionTypes } from '../../actions/internal/read-write-action-types'
import { BuilderActionTypes } from '../../builder-api/action-types'

export type { BoxDisplayModel }

export function parse(rawString: string): number {
  const value = Number(rawString.replace(/px$/, ''))

  return Number.isFinite(value) ? value : 0
}

export function createBox(boxArgs: CreateBoxArgs & { visible?: boolean }): BoxDisplayModel {
  const box = withScroll(createBoxWithoutScroll(boxArgs))
  return { ...box, visible: boxArgs.visible }
}

export function isElementVisible(element: Element): boolean {
  // checkVisibility is widely supported as of March 2024, but we're adding this
  // check as a precaution for browsers where it may not be available.
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/checkVisibility
  if (element.checkVisibility == null) {
    return true
  }

  return element.checkVisibility({
    visibilityProperty: true,
    contentVisibilityAuto: true,
    opacityProperty: true,
  })
}

export function getBox(element: Element): BoxDisplayModel {
  const visible = isElementVisible(element)
  const boxModel = withScroll(getBoxWithoutScroll(element))

  return { ...boxModel, visible }
}

export interface BoxModelHandle {
  getBoxModel(): BoxDisplayModel | null
}

export type Measurable = Element | BoxModelHandle

export function isMeasurable(value: unknown): value is Measurable {
  if (value instanceof Element) return true

  if (
    typeof value === 'object' &&
    value !== null &&
    'getBoxModel' in value &&
    typeof (value as { getBoxModel: unknown }).getBoxModel === 'function'
  ) {
    return true
  }

  return false
}

export function measure(measurable: Measurable): BoxDisplayModel | null {
  if (measurable instanceof Element) return getBox(measurable)

  return measurable.getBoxModel()
}

export type State = {
  measurables: Map<string, Map<string, Measurable>>
  boxModels: Map<string, Map<string, BoxDisplayModel>>
}

export function getInitialState(): State {
  return { measurables: new Map(), boxModels: new Map() }
}

export function getMeasurables(state: State): Map<string, Map<string, Measurable>> {
  return state.measurables
}

export function getBoxModels(state: State): Map<string, Map<string, BoxDisplayModel>> {
  return state.boxModels
}

export function getBoxModel(
  state: State,
  documentKey: string,
  elementKey: string,
): BoxDisplayModel | null {
  return getBoxModels(state).get(documentKey)?.get(elementKey) ?? null
}

export function reducer(state: State = getInitialState(), action: Action | UnknownAction) {
  if (!isKnownAction(action)) return state

  switch (action.type) {
    case ReadWriteActionTypes.REGISTER_MEASURABLE: {
      const { documentKey, elementKey, measurable } = action.payload

      return {
        ...state,
        measurables: new Map(state.measurables).set(
          documentKey,
          new Map(state.measurables.get(documentKey) ?? []).set(elementKey, measurable),
        ),
      }
    }

    case ReadWriteActionTypes.UNREGISTER_MEASURABLE: {
      const { documentKey, elementKey } = action.payload
      const nextMeasurables = new Map(state.measurables.get(documentKey) ?? [])

      const deleted = nextMeasurables.delete(elementKey)

      return deleted
        ? { ...state, measurables: new Map(state.measurables).set(documentKey, nextMeasurables) }
        : state
    }

    case BuilderActionTypes.CHANGE_ELEMENT_BOX_MODELS: {
      const { changedElementBoxModels } = action.payload

      if (changedElementBoxModels.size === 0) return state

      const nextBoxModels = new Map(state.boxModels)

      changedElementBoxModels.forEach((changedBoxModels, documentKey) => {
        const nextDocumentBoxModels = new Map(nextBoxModels.get(documentKey) ?? [])

        changedBoxModels.forEach((changedBoxModel, elementKey) => {
          if (changedBoxModel == null) nextDocumentBoxModels.delete(elementKey)
          else nextDocumentBoxModels.set(elementKey, changedBoxModel)
        })

        if (nextDocumentBoxModels.size > 0) nextBoxModels.set(documentKey, nextDocumentBoxModels)
        else nextBoxModels.delete(documentKey)
      })

      return { ...state, boxModels: nextBoxModels }
    }

    default:
      return state
  }
}
